import React from 'react';

import './assetInventoryV2.css';

export type BarSegment = {
  value: number;
  tone?: 'navy' | 'orange' | 'grey' | 'red' | 'amber';
};

export type BarItem = {
  key: string;
  label: string;
  segments: BarSegment[];
  value: React.ReactNode;
  muted?: boolean;
  // Highlights the label in red, e.g. an operating system past end of support.
  alert?: boolean;
  title?: string;
  onClick?: () => void;
};

type Props = {
  items: BarItem[];
};

const TONE_STYLES = {
  navy: 'bar-fill',
  orange: 'bar-fill bar-fill-orange',
  grey: 'bar-fill bar-fill-grey',
  red: 'bar-fill bar-fill-red',
  amber: 'bar-fill bar-fill-amber',
};

const labelStyle = (item: BarItem) => {
  if (item.alert) {
    return 'bar-label bar-label-alert';
  }
  return item.muted ? 'bar-label bar-label-muted' : 'bar-label';
};

// Ranked horizontal bars. Every bar is scaled to the largest row so lengths compare across rows.
const BarList = ({ items }: Props) => {
  const max = Math.max(
    1,
    ...items.map((item) =>
      item.segments.reduce((total, segment) => total + segment.value, 0),
    ),
  );

  return (
    <div>
      {items.map((item) => {
        const content = (
          <>
            <span styleName={labelStyle(item)} title={item.title ?? item.label}>
              {item.label}
            </span>
            <span styleName="bar">
              {item.segments.map((segment, idx) => (
                <i
                  key={idx}
                  styleName={TONE_STYLES[segment.tone ?? 'navy']}
                  style={{ width: `${(segment.value / max) * 100}%` }}
                />
              ))}
            </span>
            <span styleName="bar-num">{item.value}</span>
          </>
        );

        return item.onClick ? (
          <button
            key={item.key}
            type="button"
            styleName="bar-row bar-row-clickable"
            onClick={item.onClick}
          >
            {content}
          </button>
        ) : (
          <div key={item.key} styleName="bar-row">
            {content}
          </div>
        );
      })}
    </div>
  );
};

export default BarList;
